import { useCallback, useEffect, useState } from 'react';
import { $getSelection, $isRangeSelection, EditorState } from 'lexical';
import { $isHeadingNode, $isQuoteNode } from '@lexical/rich-text';
import { $isListNode, ListNode } from '@lexical/list';
import { $isCodeNode } from '@lexical/code';
import { $getSelectionStyleValueForProperty } from '@lexical/selection';
import { LexicalEditor } from 'lexical';

export interface ToolbarState {
  blockType: string;
  selectedElementKey: string | null;
  fontSize: string;
  fontColor: string;
  bgColor: string;
  fontFamily: string;
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isStrikethrough: boolean;
  isCode: boolean;
  canUndo: boolean;
  canRedo: boolean;
  elementAlign: string;
}

export const useToolbarState = (editor: LexicalEditor) => {
  const [toolbarState, setToolbarState] = useState<ToolbarState>({
    blockType: 'paragraph',
    selectedElementKey: null,
    fontSize: '11',
    fontColor: '#000000',
    bgColor: '#ffffff',
    fontFamily: 'Arial',
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isStrikethrough: false,
    isCode: false,
    canUndo: false,
    canRedo: false,
    elementAlign: 'left',
  });

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();

      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      // Update text formatting states
      const newState: Partial<ToolbarState> = {
        isBold: selection.hasFormat('bold'),
        isItalic: selection.hasFormat('italic'),
        isUnderline: selection.hasFormat('underline'),
        isStrikethrough: selection.hasFormat('strikethrough'),
        isCode: selection.hasFormat('code'),
        selectedElementKey: elementKey,
      };

      // Update block type
      if ($isListNode(element)) {
        const parentList = anchorNode
          .getParents()
          .find((p) => $isListNode(p)) as ListNode;
        const type = parentList
          ? parentList.getListType()
          : element.getListType();
        newState.blockType = type;
      } else {
        if ($isHeadingNode(element)) {
          newState.blockType = element.getTag();
        } else if ($isQuoteNode(element)) {
          newState.blockType = 'quote';
        } else if ($isCodeNode(element)) {
          newState.blockType = 'code';
        } else {
          newState.blockType = element.getType();
        }
      }

      // Update font styles - handle font size properly like playground
      const fontSize = $getSelectionStyleValueForProperty(
        selection,
        'font-size',
        '11px'
      );
      // Extract numeric value from font size (remove 'px' and handle various formats)
      const fontSizeValue = fontSize ? fontSize.replace(/px$/, '') : '11';
      newState.fontSize = fontSizeValue;
      newState.fontColor =
        $getSelectionStyleValueForProperty(selection, 'color', '#000000') ||
        '#000000';
      newState.bgColor =
        $getSelectionStyleValueForProperty(
          selection,
          'background-color',
          '#ffffff'
        ) || '#ffffff';
      newState.fontFamily =
        $getSelectionStyleValueForProperty(selection, 'font-family', 'Arial') ||
        'Arial';

      // Update alignment
      if (elementDOM !== null) {
        newState.elementAlign = elementDOM.style.textAlign || 'left';
      }

      setToolbarState((prev) => ({ ...prev, ...newState }));
    }
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(
      ({ editorState }: { editorState: EditorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }
    );
  }, [editor, updateToolbar]);

  useEffect(() => {
    const updateUndoRedo = () => {
      setToolbarState((prev) => ({
        ...prev,
        canUndo: true, // Simplified for now
        canRedo: true, // Simplified for now
      }));
    };

    updateUndoRedo();
    return () => {
      console.log('unmounting');
    };
  }, [editor]);

  return { toolbarState, updateToolbar };
};
